/**
 * @file LinkPosDialog.js
 * @version 1.0.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-19
 * @modified 2026-09-16
 * @description Multi-select PO-picker modal opened from LinkedPurchaseOrdersPanel.
 *              Fetches candidate POs for the parent contract / monthly_pricing
 *              row (customer-scoped, ±1 month of period) and lets the user
 *              search + tick rows + view individual POs before linking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { formatDateShort } from '../utils/dateFormatters';
import TickCheckbox from './TickCheckbox';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';
import './LinkedPurchaseOrdersPanel.css';

function LinkPosDialog({ kind, entityId, alreadyLinked, onClose, onLink, onViewPo }) {
  const urlSegment = kind === 'contract' ? 'contracts' : 'monthly';
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());

  // Measure the footer slide-out buttons. This dialog only mounts when opened,
  // so the hook lives here (not in the parent panel) — the buttons are in the DOM
  // as soon as this component commits. Re-measures when rows finish loading and
  // when the primary button's label changes with the selection count.
  useSlideOutButton([loading, pos.length, selected.size]);

  const load = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_URL}/api/pricing/${urlSegment}/${entityId}/searchable-pos`, window.location.origin);
      if (q) url.searchParams.set('q', q);
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPos(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entityId, urlSegment]);

  useEffect(() => {
    load('');
  }, [load]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => load(query), 300);
    return () => clearTimeout(t);
  }, [query, load]);

  const toggle = (poNumber) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(poNumber)) n.delete(poNumber); else n.add(poNumber);
      return n;
    });
  };

  // Pick a representative line per PO for display (first by default).
  const getDisplayLine = (po) => {
    if (!po.line_items || po.line_items.length === 0) return null;
    return po.line_items[0];
  };

  const filteredPos = pos.filter(p => !alreadyLinked.has(p.purchase_order_number));

  return createPortal(
    <div className="link-pos-dialog-overlay" onClick={onClose}>
      <div className="link-pos-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="link-pos-dialog-header">
          <h3>Link Purchase Order(s)</h3>
          <button className="btn-icon close-btn" onClick={onClose} title="Close">
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <div className="link-pos-dialog-search">
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search PO #, reference, supplier, description, or item code…"
            icon="search"
          />
        </div>

        <div className="link-pos-dialog-list">
          {loading && <div className="link-pos-dialog-empty">Loading…</div>}
          {error && <div className="link-pos-dialog-empty" style={{ color: 'var(--color-error)' }}>Failed to load: {error}</div>}
          {!loading && !error && filteredPos.length === 0 && (
            <div className="link-pos-dialog-empty">
              No candidate POs found. The search is scoped to this customer's POs within ±1 month of the contract period.
            </div>
          )}
          {!loading && filteredPos.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th style={{ width: '36px' }}></th>
                  <th>PO #</th>
                  <th>Product</th>
                  <th>Description</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Unit</th>
                  <th className="text-right">Total</th>
                  <th>Order</th>
                  <th>Delivery</th>
                  <th>Supplier</th>
                  <th style={{ width: '60px' }}>View</th>
                </tr>
              </thead>
              <tbody>
                {filteredPos.map(po => {
                  const line = getDisplayLine(po);
                  const isSel = selected.has(po.purchase_order_number);
                  return (
                    <tr key={po.purchase_order_number} className="linkable" onClick={() => toggle(po.purchase_order_number)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <TickCheckbox
                          id={`link-po-${po.purchase_order_number}`}
                          checked={isSel}
                          onChange={() => toggle(po.purchase_order_number)}
                          ariaLabel={`Select ${po.purchase_order_number}`}
                        />
                      </td>
                      <td><strong>{po.purchase_order_number}</strong></td>
                      <td>{line ? <span className="po-code-badge">{line.line_item_code || '—'}</span> : '—'}</td>
                      <td className="po-desc">{line ? (line.line_description || '—') : '—'}</td>
                      <td className="text-right">{(line && formatQuantity(line.line_quantity)) || '—'}</td>
                      <td className="text-right">{line && line.line_unit_amount != null ? formatPrice(line.line_unit_amount) : '—'}</td>
                      <td className="text-right">{line && line.line_amount != null ? formatCurrency(line.line_amount) : '—'}</td>
                      <td className="nowrap">{formatDateShort(po.date)}</td>
                      <td className="nowrap">{formatDateShort(po.delivery_date)}</td>
                      <td>{po.supplier || '—'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button className="btn-icon" title="View PO" onClick={() => onViewPo && onViewPo(po.xero_purchase_order_id)}>
                          <span className="material-symbols-rounded">visibility</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="link-pos-dialog-footer">
          <span className="selected-summary">{selected.size} selected</span>
          <button data-read-safe type="button" className="btn-round btn-danger" onClick={onClose}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
            <span className="btn-label">Cancel</span>
          </button>
          <button
            type="button"
            className="btn-slideout btn-slideout-primary"
            onClick={() => onLink(Array.from(selected))}
            disabled={selected.size === 0}
          >
            <div className="icon-wrapper"><span className="material-symbols-rounded">add_link</span></div>
            <span className="btn-label">Link {selected.size > 0 ? `${selected.size} ` : ''}PO{selected.size !== 1 ? 's' : ''}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default LinkPosDialog;
